// lib/sourceDoctrineTemplate.ts
//
// THE Source-doctrine structure (originally set up in the Source tool's
// doctrine modal). Single source of truth: the SourceDoctrineModal seeds its
// per-scope instruction files from `sourceDoctrineStructure`, and the Doctrine
// tab seeds every empty org-level Source doctrine page from `orgSourceDoctrineSeed`.
// The scoring scales themselves live on a dedicated "Barème" subpage, seeded
// from `sourceBaremeSeed` (weights mirror the DEFAULT_*_SCORING_RUBRIC constants
// in types/types.ts, and the pipeline in lib/leasing/portfolio-fit.ts +
// unit-fit-execution.ts).
export const sourceDoctrineStructure = (level: string, heading: string): string => `# ${heading}

_Everything the sourcing agent should know before scoring a brand for ${level}._
_Edit freely: the richer this document, the sharper the agent's judgement._

## Who we are looking for
- Describe the ideal tenant profile (formats, sectors, price positioning).
- What makes a brand an obvious "yes" here?

## Hard deal-breakers
- List anything that should eliminate a brand outright (e.g. direct competitor already on site, incompatible format, no expansion intent).

## Strategic priorities
1. First priority (e.g. footfall drivers).
2. Second priority (e.g. merchandising gaps to fill).
3. Third priority.

## Local context & nuance
- Catchment specifics, tenant mix, adjacencies to respect or avoid.
- Anything the numbers alone won't tell the agent.

> Tip: the scoring rubric defines *how points are counted*. This document explains *why* — the strategy behind the points.
`;

/**
 * Org-level seed for the Doctrine tab's Source doctrine page — the methodology &
 * strategy doctrine. The scoring scales live on the dedicated Barème subpage
 * (`sourceBaremeSeed`), linked from the tree and injected alongside this page.
 */
export const orgSourceDoctrineSeed = (): string => `# Sourcing Doctrine

_The operating doctrine for qualifying and scoring retail brands against this organisation's assets. It defines how we source, what we are looking for, and the standard of evidence we hold ourselves to. The scoring scales themselves are set out in the **Barème** page._

## 1. Purpose & scope

Sourcing exists to identify, qualify, and prioritise the retail brands most likely to sign, perform, and endure in our assets — and to eliminate poor fits early, before they consume analysis or negotiation effort. Every recommendation must be defensible: grounded in evidence, scored against a published rubric, and free of unsupported optimism.

## 2. The qualification funnel

Every candidate is assessed at three progressively narrower levels. A brand must earn its place at each level before it advances to the next:

1. **Portfolio Fit — WHO.** Is this brand, in itself, a credible tenant for us? Financial solidity, recent expansion momentum, and experience with our centre formats.
2. **Property Fit — WHERE (macro).** Does the brand fit *this* property? Cannibalisation / proximity risk, catchment affinity, operational logistics, and retail-mix coherence.
3. **Unit Fit — WHERE (micro).** Does the brand fit *this* unit? Surface and format, immediate adjacencies, brand preference, and footfall.

## 3. How a brand is scored

Scoring is carried out by a disciplined four-role pipeline. No single role both researches and judges:

- **The Strategist** reads the rubric and sets the research agenda, allocating effort in proportion to each criterion's weight.
- **The Detective** gathers evidence as *atomic facts* — a claim, its exact quote, and its source — from our records and the open web. A score is only ever as good as the facts beneath it.
- **The Judge**, and only the Judge, assigns points — strictly against the rubric, inventing nothing.
- **The Archivist** curates what was learned into the knowledge base, so future decisions are faster and better informed.

The scales and weights the Judge applies are defined in the **Barème**.

## 4. Standards of evidence

These are non-negotiable:

- **Cite everything.** Every material claim carries an inline source, and the full source is read — never a snippet alone.
- **No fact, no points.** A criterion cannot score high without supporting evidence; a high score with no facts behind it is a defect, not a judgement.
- **Absence is not zero.** Where evidence is missing, score conservatively and name the gap — never a blind zero, never unsupported optimism.
- **Search exhaustively.** Our records are accent-sensitive; exhaust spellings, accents, substrings, and ownership links before concluding a brand is unknown to us.
- **Act, don't narrate.** A directive becomes an executed command this turn, not a description of one.

## 5. What we are looking for

_The organisation's leasing thesis. The richer this section, the sharper the agent's judgement — the leasing team should keep it current._

- **Ideal profile:** the formats, sectors, and price positioning that fit our assets.
- **Obvious yes:** what makes a brand an unambiguous target here.

## 6. Hard deal-breakers

_Grounds for outright elimination, regardless of score — for example:_

- A direct competitor already present on site.
- An incompatible format or footprint.
- No demonstrable expansion intent on our territory.
- **No verified in-country presence or expansion.** A brand with no (or negligible) verified stores/openings in the target country is not a credible tenant candidate — regardless of the size of its declared network abroad. Absence of a nearby store is only a cannibalisation *advantage* for a brand that is actually present and expanding in-country; for an absent brand it is a viability risk and must score low on Recent expansion and neutral (not high) on Proximity/cannibalisation. Never rank an absent, non-local brand first on "no cannibalisation."

## 7. Strategic priorities

_In order of importance:_

1. First priority (e.g. footfall drivers).
2. Second priority (e.g. merchandising gaps to fill).
3. Third priority.

## 8. Local context & nuance

_What the numbers alone will not tell the agent:_

- Catchment specifics, existing tenant mix, and the adjacencies to protect or avoid.

---

_Scoring scales: see the **Barème** page._
`;

/**
 * Seed for the dedicated "Barème" subpage under the Source doctrine — the scoring
 * rubric only. Meant to feed the agent (contributesToContext), so the Judge's
 * scales are part of the injected doctrine, not just documentation.
 */
export const sourceBaremeSeed = (): string => `# Barème — Scoring Rubric

_How points are counted. This page defines the scales the Judge applies; the sourcing methodology and strategy live in the parent **Sourcing Doctrine**._

## Scoring model

Each criterion is scored **0–100** by the Judge (100 = a perfect fit on that criterion), then rescaled to the criterion's **weight** and summed into a single **fit score out of 100**:

> criterion points = round( raw₀–₁₀₀ ÷ 100 × weight )
>
> fit score = Σ criterion points  (bounded 0–100)

Because the weights within each rubric sum to 100, the fit score is itself a 0–100 index.

## Rules

- **No score-based auto-discard.** Every scored brand is retained and ranked — a weak criterion only lowers the score, it never eliminates the brand. Dropping a candidate is the leasing manager's call, not the AI's (2026-07-22 Altarea call). A brand with no verified in-country presence simply scores very low on Recent expansion and ranks last.
- **Evidence discipline.** Missing evidence scores conservatively, with the gap named; never a blind 0, never an unsupported high score.
- **Verdicts.** \`SUCCESS\` — scored and retained. \`FAILED\` — could not be scored.
- The weights below are the organisation defaults; a rubric may be tuned per scope before a campaign, through the approval-gated rubric configuration.

## Property / Asset Fit — WHO + WHERE · out of 100

_One rubric per asset campaign: the site criteria (WHERE) and the brand-quality criteria (WHO) absorbed from the retired portfolio doctrine share a single 0–100 budget._

| Criterion | Weight |
|---|---:|
| Recent expansion dynamics (verified openings/signings/investments in-country) | 25 |
| Proximity / cannibalisation risk (distance to the brand's verified in-country stores) | 20 |
| Experience with our centre formats (mall, retail park, high street…) | 20 |
| Socio-demographic affinity | 10 |
| Operational & logistics capacity (distance to the store cluster) | 10 |
| Price positioning & retail-mix fit (co-tenancy) | 10 |
| Financial health & risk | 5 |

## Unit Fit — WHERE, micro · out of 100

| Criterion | Weight |
|---|---:|
| Surface & format fit | 35 |
| Immediate tenant-mix positioning | 25 |
| Brand preference fit | 20 |
| Footfall fit | 20 |
`;

/** Version française de la doctrine de sourcing (racine). */
export const orgSourceDoctrineSeedFr = (): string => `# Doctrine de sourcing

_La doctrine opérationnelle pour qualifier et noter les enseignes commerciales au regard des actifs de cette organisation. Elle définit comment nous sourçons, ce que nous recherchons et le niveau de preuve que nous nous imposons. Les barèmes de notation, eux, figurent sur la page **Barème**._

## 1. Objet & périmètre

Le sourcing existe pour identifier, qualifier et prioriser les enseignes les plus susceptibles de signer, de performer et de durer dans nos actifs — et pour écarter tôt les mauvaises adéquations, avant qu'elles ne consomment de l'analyse ou de la négociation. Toute recommandation doit être défendable : étayée par des preuves, notée selon un barème publié, et exempte d'optimisme non fondé.

## 2. L'entonnoir de qualification

Chaque candidat est évalué à trois niveaux de plus en plus fins. Une enseigne doit mériter sa place à chaque niveau avant de passer au suivant :

1. **Adéquation Portefeuille — QUI.** Cette enseigne est-elle, en soi, un locataire crédible pour nous ? Solidité financière, dynamique d'expansion récente, expérience de nos formats de centres.
2. **Adéquation Site — OÙ (macro).** L'enseigne convient-elle à *ce* site ? Risque de cannibalisation / proximité, affinité de la zone de chalandise, logistique opérationnelle, cohérence du mix commercial.
3. **Adéquation Emplacement — OÙ (micro).** L'enseigne convient-elle à *cet* emplacement ? Surface et format, adjacences immédiates, préférence de la marque, flux.

## 3. Comment une enseigne est notée

La notation est réalisée par un pipeline discipliné à quatre rôles. Aucun rôle ne fait à la fois la recherche et le jugement :

- **Le Stratège** lit le barème et fixe le programme de recherche, en allouant l'effort au prorata du poids de chaque critère.
- **Le Détective** collecte les preuves sous forme de *faits atomiques* — une affirmation, sa citation exacte et sa source — à partir de nos données et du web. Une note ne vaut que ce que valent les faits qui la fondent.
- **Le Juge**, et lui seul, attribue les points — strictement selon le barème, sans rien inventer.
- **L'Archiviste** consigne les enseignements dans la base de connaissance, pour rendre les décisions futures plus rapides et mieux informées.

Les barèmes et poids appliqués par le Juge sont définis sur la page **Barème**.

## 4. Exigences de preuve

Non négociables :

- **Tout sourcer.** Toute affirmation matérielle porte une source en lien ; on lit la source complète, jamais un simple extrait.
- **Pas de fait, pas de points.** Un critère ne peut obtenir une note élevée sans preuve ; une note élevée sans fait est un défaut, pas un jugement.
- **L'absence n'est pas zéro.** En l'absence de preuve, on note prudemment en signalant la lacune — jamais un zéro aveugle, jamais un optimisme non étayé.
- **Chercher de façon exhaustive.** Nos données sont sensibles aux accents ; épuiser orthographes, accents, sous-chaînes et liens de propriété avant de conclure qu'une enseigne nous est inconnue.
- **Agir, ne pas commenter.** Une directive devient une commande exécutée dans le tour même, et non sa description.

## 5. Ce que nous recherchons

_La thèse de leasing de l'organisation. Plus cette section est riche, plus le jugement de l'agent est fin — l'équipe leasing doit la tenir à jour._

- **Profil idéal :** les formats, secteurs et positionnements prix qui conviennent à nos actifs.
- **Oui évident :** ce qui fait d'une enseigne une cible incontestable ici.

## 6. Éliminatoires

_Motifs d'élimination immédiate, quel que soit le score — par exemple :_

- Un concurrent direct déjà présent sur le site.
- Un format ou une surface incompatibles.
- Aucune intention d'expansion démontrable sur notre territoire.
- **Aucune présence ni expansion vérifiée dans le pays.** Une enseigne sans magasins/ouvertures vérifiés (ou négligeables) dans le pays cible n'est pas un candidat locataire crédible — quelle que soit la taille de son réseau déclaré à l'étranger. L'absence de magasin à proximité n'est un *avantage* anti-cannibalisation que pour une enseigne réellement présente et en expansion dans le pays ; pour une enseigne absente, c'est un risque de viabilité : note basse en Dynamique d'Expansion et neutre (pas haute) en Proximité/cannibalisation. Ne jamais classer premier une enseigne étrangère absente au motif « aucune cannibalisation ».

## 7. Priorités stratégiques

_Par ordre d'importance :_

1. Première priorité (ex. générateurs de flux).
2. Deuxième priorité (ex. lacunes de merchandising à combler).
3. Troisième priorité.

## 8. Contexte & nuances locales

_Ce que les chiffres seuls ne diront pas à l'agent :_

- Spécificités de la zone de chalandise, mix locatif existant, adjacences à protéger ou à éviter.

---

_Barèmes de notation : voir la page **Barème**._
`;

/** Version française du barème (grille de notation). */
export const sourceBaremeSeedFr = (): string => `# Barème — Grille de notation

_Comment les points sont comptés. Cette page définit les barèmes appliqués par le Juge ; la méthodologie et la stratégie de sourcing figurent sur la page parente **Doctrine de sourcing**._

## Modèle de notation

Chaque critère est noté de **0 à 100** par le Juge (100 = adéquation parfaite sur ce critère), puis ramené au **poids** du critère et sommé en un **score d'adéquation sur 100** :

> points du critère = arrondi( brut₀–₁₀₀ ÷ 100 × poids )
>
> score d'adéquation = Σ points des critères  (borné entre 0 et 100)

Comme les poids de chaque grille totalisent 100, le score d'adéquation est lui-même un indice de 0 à 100.

## Règles

- **Aucune élimination automatique par le score.** Toute enseigne notée est conservée et classée — un critère faible baisse la note, il n'élimine jamais l'enseigne. Écarter un candidat relève du leasing manager, pas de l'IA (échange Altarea du 22/07/2026). Une enseigne sans présence vérifiée dans le pays obtient simplement une note très basse en Dynamique d'expansion et se classe en dernier.
- **Discipline de la preuve.** Une preuve manquante est notée prudemment, la lacune étant signalée ; jamais un 0 aveugle, jamais une note élevée non étayée.
- **Verdicts.** \`SUCCESS\` — notée et retenue. \`FAILED\` — impossible à noter.
- Les poids ci-dessous sont les valeurs par défaut de l'organisation ; une grille peut être ajustée par périmètre avant une campagne, via la configuration (soumise à validation).

## Adéquation Site / Actif — QUI + OÙ · sur 100

_Une seule grille par campagne d'actif : les critères de site (OÙ) et les critères de qualité de marque (QUI), repris de la doctrine portefeuille retirée, partagent un même budget de 0 à 100._

| Critère | Poids |
|---|---:|
| Dynamique d'expansion récente (ouvertures, signatures, investissements vérifiés dans le pays) | 25 |
| Risque de proximité / cannibalisation (distance aux points de vente vérifiés de la marque dans le pays) | 20 |
| Expérience de nos formats de centres (centre commercial, retail park, high street…) | 20 |
| Affinité socio-démographique | 10 |
| Capacité opérationnelle & logistique (distance au cluster de magasins) | 10 |
| Positionnement prix & cohérence du mix commercial (co-tenancy) | 10 |
| Santé financière & risque | 5 |

## Adéquation Emplacement — OÙ, micro · sur 100

| Critère | Poids |
|---|---:|
| Adéquation surface & format | 35 |
| Positionnement dans le mix locatif immédiat | 25 |
| Préférence de la marque | 20 |
| Adéquation au flux (footfall) | 20 |
`;
